<template>
  <b-modal :title="helper.trans('labels.receipt_verification')"
           :modelValue="modal !== null"
           footer-class="d-flex justify-content-between"
           @update:modelValue="$event === false && (modal = null)">
    <template #default>
      <div v-if="stopScamEnabled" class="mb-3">
        <div class="row mb-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('stopscam.manual.payment_method') }}</label>
          <div class="col-sm-8">
            <select v-model="form.paymentMethod" class="form-select form-select-sm">
              <option v-for="method in paymentMethods" :key="method" :value="method">{{ helper.trans(`stopscam.manual.${method}`) }}</option>
            </select>
          </div>
        </div>
        <div class="row mb-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('stopscam.manual.requisite') }}</label>
          <div class="col-sm-8">
            <input v-model="form.requisite" type="text" class="form-control form-control-sm">
          </div>
        </div>
        <div class="row mb-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('stopscam.manual.bank_name') }}</label>
          <div class="col-sm-8">
            <input v-model="form.bankName" type="text" class="form-control form-control-sm">
          </div>
        </div>
        <div class="row mb-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('stopscam.manual.amount') }}</label>
          <div class="col-sm-8">
            <input v-model="form.amount" type="text" inputmode="decimal" class="form-control form-control-sm">
          </div>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-sm-12">
          <div style="cursor: pointer; border:1px dashed" v-bind="dropzone.getRootProps()" class="payout-dropzone rounded border-secondary p-3 pt-4 pb-4 d-flex justify-content-center align-items-center">
            <input v-bind="dropzone.getInputProps()" />
            <p class="p-0 m-0">{{ helper.trans('labels.payout_upload_files')}}</p>
          </div>
        </div>
      </div>
      <div v-for="fileAttachment in fileAttachments" class="row mb-2 ms-2" :key="fileAttachment.id">
        <div class="col-sm-12">
          <i class="fa fa-paperclip"></i> <span class="me-2">{{ fileAttachment.file.name}}</span>
          <div v-if="fileAttachment.error === null && fileAttachment.attachment === null" class="spinner-border" role="status" style="zoom: 0.5">
            <span class="visually-hidden"></span>
          </div>
          <a v-else href="#" @click.prevent="removeFile(fileAttachment)">
            <i class="fa fa-times"></i>
          </a>
          <div>
            <small class="text-danger" v-if="fileAttachment.error !== null">{{fileAttachment.error}}</small>
          </div>
        </div>
        <div class="col-sm-12">
          <canvas v-if="fileAttachment.isPdf" :id="`pdf-canvas-${fileAttachment.id}`" class="w-100"></canvas>
          <img v-else-if="fileAttachment.previewUrl" :src="fileAttachment.previewUrl" class="w-100" alt="">
        </div>
        <div class="col-sm-12" v-if="stopScamEnabled && fileAttachment.attachment && fileAttachment.error === null">
          <small v-if="fileAttachment.attachment.passed" class="text-success d-block">
            <i class="fa-solid fa-circle me-1"></i>{{ helper.trans('stopscam.passed') }}
          </small>
          <template v-else>
            <small class="text-danger d-block">
              <i class="fa-solid fa-circle me-1"></i>{{ helper.trans('stopscam.failed') }}
            </small>
            <small v-for="verdict in fileAttachment.attachment.verdicts" :key="verdict" class="text-danger d-block ms-3">
              {{ helper.trans(`stopscam.verdict.${verdict}`, {}, verdict) }}
            </small>
          </template>
        </div>
        <div class="col-sm-12" v-else-if="fileAttachment.attachment">
          <small class="text-secondary" style="word-break: break-all;">
              {{JSON.stringify(fileAttachment.attachment)}}
          </small>
        </div>
      </div>
    </template>
    <template #footer>
      <button type="button" @click="modal = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.ok')  }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import { useDropzone } from "vue3-dropzone";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import CopyButton from "@/components/CopyButton.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import {useStore} from "vuex";

const STOPSCAM_PAYMENT_METHODS = ['card', 'sbp', 'account_number'];

const SUPPORTED_RECEIPT_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];

interface AttachmentFileDTO {
  id: number;
  attachment: any,
  file: File,
  error: string|null,
  isPdf: boolean,
  previewUrl: string|null
}

const isPdfFile = (file: File): boolean => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

const api = new APIClient();
const notification = useNotification()
let id = 1;

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    CopyButton
  },
  setup() {

    window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

    const store = useStore()
    const stopScamEnabled = computed(() => !!store.getters.getAppData?.af_stopscam_payout_receipt_enabled)

    const state = reactive({
      modal: null as any,
      fileAttachments: [] as AttachmentFileDTO[],
      form: {
        paymentMethod: 'card',
        requisite: '',
        bankName: '',
        amount: '',
      },
    });

    const stopScamErrorMessage = (e: any): string => {
      if (e.response?.status === 400 && Array.isArray(e.response?.data) && e.response.data.length > 0) {
        return e.response.data[0].message ?? helper.trans('stopscam.checker_unavailable')
      }

      return helper.trans('stopscam.checker_unavailable')
    }

    const getBase64 = (attachment:AttachmentFileDTO) => {
      var reader = new FileReader();
      reader.readAsDataURL(attachment.file);
      reader.onload = () => {
        if (!reader.result) {
          return;
        }

        const pdfData = atob(reader.result.toString().replace('data:application/pdf;base64,', ''))
        const loadingTask = window.pdfjsLib.getDocument({data: pdfData});
        loadingTask.promise.then((pdf:any) => {
          pdf.getPage(1).then((page:any) => {
            const scale = 1.5;
            const viewport = page.getViewport({scale: scale});

            const canvas = document.getElementById('pdf-canvas-' + attachment.id) as HTMLCanvasElement
            if (!canvas) {
              return;
            }
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const renderTask = page.render({
              canvasContext: context,
              viewport: viewport
            });
            renderTask.promise.then(() => {
            });
          });
        }, (reason: any) => {
          console.error(reason);
        });
      };
      reader.onerror = error => {};
    }

    const onDrop = async (acceptFiles: any, rejectReasons: any) => {
      for (const file of acceptFiles) {
        const isPdf = isPdfFile(file)
        state.fileAttachments.push({
          id: id++,
          attachment: null,
          file,
          error: isPdf || stopScamEnabled.value ? null : helper.trans('server_errors.PAY_OUT_ATTACHMENTS_PDF_REQUIRED'),
          isPdf,
          previewUrl: isPdf ? null : URL.createObjectURL(file)
        } as AttachmentFileDTO)
      }

      for (const fileAttachment of state.fileAttachments) {
        if (stopScamEnabled.value && (fileAttachment.attachment !== null || fileAttachment.error !== null)) {
          continue
        }

        if (!fileAttachment.isPdf && !stopScamEnabled.value) {
          continue
        }

        try {
          if (fileAttachment.isPdf) {
            try {
              getBase64(fileAttachment)
            } catch (e) {}
          }
          fileAttachment.attachment = await api.receiptVerification(fileAttachment.file, stopScamEnabled.value ? {...state.form} : null)
          console.log(fileAttachment.attachment)
        } catch (e: any) {
          if (stopScamEnabled.value) {
            fileAttachment.error = stopScamErrorMessage(e)
            continue
          }

          fileAttachment.error = helper.trans(`server_errors.c8c7315c-6186-4719-8b71-5659e16bdcb7`)
          fileAttachment.attachment = e.response?.data
        }

      }
    }
    const dropzone = useDropzone({ onDrop, accept: SUPPORTED_RECEIPT_EXTENSIONS });

    const getForm = () => {
      return {
        attachments: state.fileAttachments.filter((file) => file.attachment !== null).map((file) => file.attachment!.id)
      }
    }

    const show = () => {
      state.modal = true
      state.fileAttachments = [];
    }

    const removeFile = (fileAttachment: AttachmentFileDTO) => {
      state.fileAttachments = state.fileAttachments.filter((file) => file.id !== fileAttachment.id)
    }

    return {
      ...toRefs(state),
      show,
      helper,
      dropzone,
      getForm,
      removeFile,
      numberFormatter,
      stopScamEnabled,
      paymentMethods: STOPSCAM_PAYMENT_METHODS
    }
  }
})
</script>
